// Brand facts that drive the Brand Assets page. These are deliberately hardcoded
// constants rather than CMS content - they're the canonical definition of the
// brand, not editor copy. Editable copy (hero/meta/download URL) lives in
// SiteSettings -> Listing Pages -> Brand Assets.

export type LogoBackground = {
  key: 'aurora' | 'white' | 'cloud' | 'black'
  label: string
  /** Panel background colour. */
  bg: string
  /** Which logo variant reads with contrast on this background. */
  logoVariant: 'black' | 'white'
  /** Swatch border for light swatches that would otherwise vanish. */
  swatchBordered?: boolean
}

export const LOGO_BACKGROUNDS: LogoBackground[] = [
  { key: 'aurora', label: 'Aurora', bg: '#00FF6F', logoVariant: 'black' },
  { key: 'white', label: 'White', bg: '#FFFFFF', logoVariant: 'black', swatchBordered: true },
  { key: 'cloud', label: 'Cloud', bg: '#D1CCBE', logoVariant: 'black' },
  { key: 'black', label: 'Black', bg: '#000000', logoVariant: 'white' },
]

export type PaletteColour = {
  name: string
  hex: string
  rgba: string
  cmyk: string
  /** Card background. */
  bg: string
  /** 'light' cards use black text, 'dark' cards use white text. */
  tone: 'light' | 'dark'
  /** White needs a hairline border to separate from the page. */
  bordered?: boolean
}

export const PRIMARY_PALETTE: PaletteColour[] = [
  {
    name: 'White',
    hex: '#FFFFFF',
    rgba: 'RGBA 255 · 255 · 255 · 1',
    cmyk: 'CMYK 0 · 0 · 0 · 0',
    bg: '#FFFFFF',
    tone: 'light',
    bordered: true,
  },
  {
    name: 'Cloud',
    hex: '#D1CCBE',
    rgba: 'RGBA 209 · 204 · 190 · 1',
    cmyk: 'CMYK 0 · 36 · 93 · 0',
    bg: '#D1CCBE',
    tone: 'light',
  },
  {
    name: 'Black',
    hex: '#000000',
    rgba: 'RGBA 0 · 0 · 0 · 1',
    cmyk: 'CMYK 60 · 40 · 40 · 100',
    bg: '#000000',
    tone: 'dark',
  },
  {
    name: 'Aurora',
    hex: '#00FF6F',
    rgba: 'RGBA 0 · 255 · 111 · 1',
    cmyk: 'CMYK 70 · 0 · 80 · 0',
    bg: '#00FF6F',
    tone: 'light',
  },
]

export type SubBrand = {
  key: 'public' | 'private' | 'ai'
  /** Suffix shown after the "civo" wordmark, e.g. "public". */
  suffix: string
  description: string
  hex: string
  rgba: string
  cmyk: string
  bg: string
  /** Intrinsic SVG viewBox dimensions - passed to next/image so it reserves the correct
      aspect ratio (avoids layout shift). Display height comes from logoHeight + w-auto. */
  width: number
  height: number
  /** Rendered logo height in px. Per-logo because the wordmarks crop to different
      heights (ai has no descender), so a single height looks inconsistent. The slot
      around the logo is fixed (SUB_BRAND_LOGO_SLOT) so shorter logos don't shift layout. */
  logoHeight: number
}

/** Fixed height of the logo slot on each card - the tallest logo - so per-logo
    height differences never move the content below. */
export const SUB_BRAND_LOGO_SLOT = 39

export const SUB_BRANDS: SubBrand[] = [
  {
    key: 'public',
    suffix: 'public',
    description:
      'Enterprise-ready public cloud that puts sovereignty, cost and ideas back in your control.',
    hex: '#D46FFE',
    rgba: 'RGBA 212 · 111 · 254 · 1',
    cmyk: 'CMYK 17 · 56 · 0 · 0',
    bg: '#D46FFE',
    width: 2826,
    height: 666,
    logoHeight: 39,
  },
  {
    key: 'private',
    suffix: 'private',
    description:
      'Dedicated, single-tenant cloud for teams that need control, isolation and compliance.',
    hex: '#FF7633',
    rgba: 'RGBA 255 · 118 · 51 · 1',
    cmyk: 'CMYK 0 · 54 · 80 · 0',
    bg: '#FF7633',
    width: 3092,
    height: 666,
    logoHeight: 39,
  },
  {
    key: 'ai',
    suffix: 'ai',
    description:
      'GPU-accelerated platform for building, training and shipping AI - fast and predictably.',
    hex: '#33B7A3',
    rgba: 'RGBA 51 · 183 · 163 · 1',
    cmyk: 'CMYK 72 · 0 · 11 · 28',
    bg: '#33B7A3',
    width: 1646,
    height: 505,
    logoHeight: 32,
  },
]

export type LogoDont = {
  title: string
  why: string
  /** How the MaskedLogo is distorted to illustrate the misuse. */
  treatment: 'stretch' | 'outline' | 'recolour' | 'lowContrast' | 'rotate' | 'effects'
}

export const LOGO_DONTS: LogoDont[] = [
  { title: 'Stretch', why: 'Keep the original proportions.', treatment: 'stretch' },
  { title: 'Outline', why: 'Use the solid mark.', treatment: 'outline' },
  { title: 'Recolour', why: 'Only approved brand colours.', treatment: 'recolour' },
  { title: 'Low contrast', why: 'Maintain strong contrast.', treatment: 'lowContrast' },
  { title: 'Rotate', why: 'Keep it upright and level.', treatment: 'rotate' },
  { title: 'Effects', why: 'No shadows, gradients or patterns.', treatment: 'effects' },
]

export type TypeSpecimen = {
  family: string
  role: string
  fontClass: string
  weights: string[]
  tracking: string
  sample: string
}

export const TYPE_SPECIMENS: TypeSpecimen[] = [
  {
    family: 'Hanken Grotesk',
    role: 'Primary · interface & body',
    fontClass: 'font-sans',
    weights: ['Regular', 'Medium'],
    tracking: 'Tracking  -0.6 to -0.7',
    sample: 'The quick brown fox jumps over the lazy dog. 0123456789',
  },
  {
    family: 'Newsreader',
    role: 'Display · editorial accents',
    fontClass: 'font-display',
    weights: ['Light'],
    tracking: 'Tracking  -1%',
    sample: 'The quick brown fox jumps over the lazy dog. 0123456789',
  },
]

export type TypeAvoid = {
  caption: string
  treatment: 'caps' | 'tracking' | 'weight' | 'contrast'
}

export const TYPE_AVOID: TypeAvoid[] = [
  { treatment: 'caps', caption: 'Never use ALL CAPS' },
  { treatment: 'tracking', caption: 'Be mindful of letter spacing, don’t go too tight' },
  { treatment: 'weight', caption: 'Only use Newsreader in light' },
  { treatment: 'contrast', caption: 'Avoid low contrast colour combinations for type' },
]
